import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";

/**
 * Shared Deployments dropdown for proposal-page navs.
 *
 * Lets a reviewer cleanly switch between deployments WITHOUT a confusing
 * top-bar "PROPOSAL 01" button that looks like a CTA. Closes on outside
 * click or ESC.
 *
 * Props:
 *   - currentId: "baytown" | "north-houston"  (highlights the current entry)
 *   - scrolled:  boolean (light/dark state from parent nav)
 */
export default function DeploymentSwitcher({ currentId, scrolled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const items = [
    { id: "baytown", to: "/baytown", num: "01", label: "Baytown, TX", scale: "1,500 households · $4.13M" },
    { id: "north-houston", to: "/north-houston", num: "02", label: "North Houston District", scale: "1,200 households · $3.46M" },
  ];

  return (
    <div className="relative" ref={ref} data-testid="deployment-switcher">
      <button
        type="button"
        data-testid="deployment-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`flex items-center gap-1.5 h-9 px-3 font-mono text-[11px] uppercase tracking-widest border transition-colors ${
          scrolled
            ? "border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F7F1]"
            : "border-[#F9F7F1]/40 text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F]"
        }`}
      >
        Proposals <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="deployment-switcher-menu"
          role="menu"
          className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#CBD5E1] shadow-2xl z-[60]"
        >
          <div className="px-4 py-2.5 border-b border-[#CBD5E1] bg-[#F9F7F1]">
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000]">
              Switch proposal dossier
            </span>
          </div>
          {items.map((it) => {
            const active = it.id === currentId;
            return (
              <Link
                key={it.id}
                to={it.to}
                data-testid={`deployment-switcher-link-${it.id}`}
                onClick={() => setOpen(false)}
                className={`block px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 transition-colors ${
                  active ? "bg-[#FBF6E5]" : "bg-white hover:bg-[#F9F7F1]"
                }`}
                aria-current={active ? "page" : undefined}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000]">
                    Proposal {it.num}{active ? " · Viewing" : ""}
                  </span>
                  {!active && <ArrowRight className="h-3 w-3 text-[#8B0000] shrink-0" />}
                </div>
                <div className="font-display text-base text-[#0A192F] leading-snug">
                  {it.label}
                </div>
                <div className="text-[11px] text-[#475569] mt-1">{it.scale}</div>
              </Link>
            );
          })}
          <Link
            to="/compare"
            data-testid="deployment-switcher-compare"
            onClick={() => setOpen(false)}
            className="block px-4 py-3 bg-[#FBF6E5] border-t border-[#D4AF37]/40 hover:bg-[#F5EBC8] transition-colors"
          >
            <div className="flex items-baseline justify-between gap-2 mb-0.5">
              <span className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000]">
                Side-by-Side Comparison
              </span>
              <ArrowRight className="h-3 w-3 text-[#8B0000] shrink-0" />
            </div>
            <div className="font-display text-sm text-[#0A192F] leading-snug">
              Compare both proposals
            </div>
            <div className="text-[11px] text-[#475569] mt-0.5">Budget · workforce · outcomes · regional case</div>
          </Link>
          <Link
            to="/"
            data-testid="deployment-switcher-home"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 bg-[#0A192F] text-[#F9F7F1] font-mono text-[10px] uppercase tracking-widest hover:bg-[#1F2937]"
          >
            ← Back to The Bridge Model home
          </Link>
        </div>
      )}
    </div>
  );
}
